// client/src/hooks/useNotificationBadge.js
import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import ApiNotifications from '../api/ApiNotifications'
import { queryKeys } from '../queryClient'

/**
 * Custom hook to manage unread notification count badge
 * Uses React Query for automatic caching, polling, and deduplication
 *
 * @param {number|null} pollInterval - Polling interval in milliseconds (default: 60000 = 1 minute). Pass null to disable polling.
 * @returns {Object} - { count, loading, error, refresh }
 */
function useNotificationBadge(pollInterval = 60000) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.notificationCount(),
    queryFn: async () => {
      const response = await ApiNotifications.getUnreadCount()
      if (
        response?.success !== true ||
        typeof response?.data?.count !== 'number'
      ) {
        const errorMessage =
          response?.error?.message ||
          response?.error ||
          'Failed to fetch unread count'
        throw new Error(errorMessage)
      }

      return response.data.count
    },
    // Polling configuration
    refetchInterval: pollInterval,
    // Disable query when polling is disabled
    enabled: pollInterval !== null,
    // Keep data fresh based on poll interval
    staleTime: pollInterval || 60000,
    // Don't reset to loading state on refetch
    placeholderData: (previousData) => previousData,
  })

  // Refetch notification count when tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('Tab became visible, refreshing notification count...')
        refetch()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [refetch])

  return {
    count: data || 0,
    loading: isLoading,
    error: error?.message || null,
    refresh: refetch,
  }
}

export default useNotificationBadge
